import * as React from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Typography, Box } from '@mui/material'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import ReactApexChart from 'react-apexcharts'
import mermaid from 'mermaid'
import CodeBlock from './CodeBlock'
import PresentationViewer from './PresentationViewer'

const parseHeatmapBlock = (raw) => {
  const cleaned = String(raw || '').trim()
  if (!cleaned) {
    return { error: 'Heatmap block is empty.' }
  }

  const lines = cleaned
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean)

  let title = ''
  let ranges = null
  const series = []
  const nonTitleLines = []

  lines.forEach((line) => {
    const titleMatch = line.match(/^title\s*:\s*(.+)$/i)
    if (titleMatch) {
      const rawTitle = titleMatch[1].trim()
      const parts = rawTitle.split('|').map(part => part.trim()).filter(Boolean)
      title = parts[0] || ''
      if (parts.length > 1) {
        ranges = parseHeatmapRanges(parts.slice(1))
      }
      return
    }

    const rangesMatch = line.match(/^ranges\s*:\s*(.+)$/i)
    if (rangesMatch) {
      const rawRanges = rangesMatch[1]
        .split('|')
        .map(part => part.trim())
        .filter(Boolean)
      ranges = parseHeatmapRanges(rawRanges)
      return
    }

    nonTitleLines.push(line)
  })

  const looksLikeCsv = nonTitleLines.length > 1 && nonTitleLines[0].includes(',')
  if (looksLikeCsv) {
    const headers = nonTitleLines[0]
      .split(',')
      .map(item => item.trim())
      .filter(Boolean)

    if (headers.length < 2) {
      return { error: 'Heatmap CSV header must be like: category,series1,series2' }
    }

    const seriesNames = headers.slice(1)
    seriesNames.forEach((name) => {
      series.push({ name, data: [] })
    })

    nonTitleLines.slice(1).forEach((line) => {
      const cells = line
        .split(',')
        .map(item => item.trim())
      if (cells.length < 2) {
        return
      }

      const x = cells[0]
      const values = cells.slice(1)

      series.forEach((entry, index) => {
        const parsed = Number(values[index])
        entry.data.push({
          x,
          y: Number.isNaN(parsed) ? null : parsed
        })
      })
    })

    if (!series.length) {
      return {
        error: 'Heatmap CSV must include at least one series column.'
      }
    }

    return { title, series, ranges: ranges || HEATMAP_RANGES }
  }

  let xLabels = []
  nonTitleLines.forEach((line) => {
    const separator = line.indexOf(':')
    if (separator < 0) {
      return
    }

    const key = line.slice(0, separator).trim()
    const value = line.slice(separator + 1).trim()

    if (!key || !value) {
      return
    }

    if (key.toLowerCase() === 'x') {
      xLabels = value
        .split(',')
        .map(item => item.trim())
        .filter(Boolean)
      return
    }

    const values = value
      .split(',')
      .map(item => Number(item.trim()))
      .filter(item => !Number.isNaN(item))

    series.push({
      name: key,
      data: values.map((y, index) => ({
        x: xLabels[index] || `${index + 1}`,
        y
      }))
    })
  })

  if (!series.length) {
    return {
      error: 'Heatmap must include rows, e.g. "Team A: 10,20,30", or CSV format.'
    }
  }

  return { title, series, ranges: ranges || HEATMAP_RANGES }
}

const HEATMAP_RANGES = [
  { from: -1000000, to: 0, name: 'Failed', color: '#ef4444' },
  { from: 1, to: 200, name: 'Low', color: '#f59e0b' },
  { from: 201, to: 1000000, name: 'High', color: '#22c55e' }
]

const pickRangeColor = (name, index) => {
  const key = String(name || '').toLowerCase()
  if (key.includes('fail')) return '#ef4444'
  if (key.includes('low')) return '#f59e0b'
  if (key.includes('high')) return '#22c55e'
  const palette = ['#38bdf8', '#a78bfa', '#f97316', '#eab308', '#14b8a6']
  return palette[index % palette.length]
}

const parseHeatmapRanges = (rawParts) => {
  const parsed = []
  rawParts.forEach((part, index) => {
    const clean = String(part || '').trim()
    if (!clean) return
    const colorMatch = clean.match(/^(.*?)(?:@)?\s*(#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})|[a-zA-Z]+)$/)
    const body = colorMatch ? colorMatch[1].trim() : clean
    const customColor = colorMatch ? colorMatch[2].trim() : null

    const between = body.match(/^([^:]+):\s*(-?\d+(?:\.\d+)?)\s*-\s*(-?\d+(?:\.\d+)?)$/)
    if (between) {
      parsed.push({
        name: between[1].trim(),
        from: Number(between[2]),
        to: Number(between[3]),
        color: customColor || pickRangeColor(between[1], index)
      })
      return
    }

    const lte = body.match(/^([^<>=]+)<=\s*(-?\d+(?:\.\d+)?)$/)
    if (lte) {
      parsed.push({
        name: lte[1].trim(),
        from: -1000000,
        to: Number(lte[2]),
        color: customColor || pickRangeColor(lte[1], index)
      })
      return
    }

    const gte = body.match(/^([^<>=]+)>=\s*(-?\d+(?:\.\d+)?)$/)
    if (gte) {
      parsed.push({
        name: gte[1].trim(),
        from: Number(gte[2]),
        to: 1000000,
        color: customColor || pickRangeColor(gte[1], index)
      })
    }
  })

  return parsed.length ? parsed : HEATMAP_RANGES
}

const getHeatmapCategory = (value, ranges) => {
  const match = ranges.find(range => value >= range.from && value <= range.to)
  return match ? match.name : 'N/A'
}

const formatSeconds = (value) => {
  const totalSeconds = Math.max(0, Math.round(Number(value)))
  if (totalSeconds === 0) {
    return ''
  }
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}min${seconds}sec`
}

const formatHeatmapTooltip = (value, ranges) => {
  const totalSeconds = Math.max(0, Math.round(Number(value)))
  if (totalSeconds === 0 || totalSeconds === 360000) {
    return '(fail)'
  }
  return `${formatSeconds(totalSeconds)} (${getHeatmapCategory(totalSeconds, ranges)})`
}

const HeatmapChart = ({ raw }) => {
  const parsed = parseHeatmapBlock(raw)
  if (parsed.error) {
    return (
      <pre style={{ margin: '0 0 1rem 0' }}>
        <code>{parsed.error}</code>
      </pre>
    )
  }

  const rowCount = parsed.series.length || 0
  const rowHeightPx = 22
  const chartHeight = Math.max(180, rowCount * rowHeightPx + 70)

  const options = {
    chart: {
      background: 'transparent',
      fontFamily: 'Radar, sans-serif',
      toolbar: { show: false },
      animations: { enabled: true }
    },
    title: {
      text: parsed.title || '',
      offsetY: 0,
      style: { color: '#ffffff', fontSize: '12px', fontWeight: 600, fontFamily: 'Radar, sans-serif' }
    },
    dataLabels: { enabled: false },
    stroke: { width: 1, colors: ['#ffffff'] },
    plotOptions: {
      heatmap: {
        enableShades: false,
        colorScale: {
          ranges: parsed.ranges
        }
      }
    },
    xaxis: {
      labels: { style: { colors: '#ffffff', fontSize: '9px', fontFamily: 'Radar, sans-serif' } }
    },
    yaxis: {
      labels: { style: { colors: '#ffffff', fontSize: '11px', fontFamily: 'Radar, sans-serif' } }
    },
    tooltip: {
      y: {
        formatter: (value) => formatHeatmapTooltip(value, parsed.ranges)
      }
    },
    legend: {
      show: true,
      labels: { colors: '#ffffff' },
      fontFamily: 'Radar, sans-serif',
      fontSize: '13px',
      itemMargin: { horizontal: 8, vertical: 8 },
      markers: {
        strokeWidth: 0,
        strokeColor: 'transparent'
      }
    },
    theme: { mode: 'dark' }
  }

  return (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'center', margin: '0 0 0 0' }}>
      <div style={{ width: 'min(100%, 900px)' }}>
        <ReactApexChart
          options={options}
          series={parsed.series}
          type="heatmap"
          height={chartHeight}
        />
      </div>
    </div>
  )
}

const MermaidChart = ({ raw }) => {
  const [svg, setSvg] = React.useState('')
  const [error, setError] = React.useState('')

  React.useEffect(() => {
    let mounted = true

    const renderMermaid = async () => {
      const source = String(raw || '').trim()
      if (!source) {
        if (mounted) {
          setError('Mermaid block is empty.')
          setSvg('')
        }
        return
      }

      try {
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          theme: 'dark'
        })
        const id = `mermaid-${Math.random().toString(36).slice(2)}`
        const result = await mermaid.render(id, source)
        if (mounted) {
          setSvg(result.svg)
          setError('')
        }
      } catch (err) {
        if (mounted) {
          setError(`Mermaid parse error: ${err?.message || 'invalid syntax'}`)
          setSvg('')
        }
      }
    }

    renderMermaid()

    return () => {
      mounted = false
    }
  }, [raw])

  if (error) {
    return (
      <pre style={{ margin: '0 0 1rem 0' }}>
        <code>{error}</code>
      </pre>
    )
  }

  return (
    <div style={{ width: '100%', display: 'flex', justifyContent: 'center', margin: '0 0 1rem 0' }}>
      <div
        style={{ width: 'min(100%, 900px)', overflowX: 'auto' }}
        dangerouslySetInnerHTML={{ __html: svg }}
      />
    </div>
  )
}

const Page = () => {
  const doc = useParams().doc
  const notes = useSelector(state => state.data.notesMap)
  if (!notes || !notes[doc]) {
    return <div />
  }
  const note = notes[doc]
  const title = note.name
  const date = note.date
  const type = note.type || 'markdown'
  const content = note.content || ''

  if (type === 'presentation') {
    return (
      <PresentationViewer
        date={date}
        slides={note.slides || []}
      />
    )
  }

  return (
    <div>
      <Box sx={{
        justifyItems: 'center',
        marginBottom: '1rem',
        borderBottom: '1px solid darkgrey',
      }}>
        <Typography component="h1" sx={{
          margin: 0,
          fontSize: '1.5em',
          fontWeight: 'bold',
        }}>{title}</Typography>
        <Typography sx={{
          fontSize: '10px',
          lineHeight: '17px',
          color: 'darkgrey',
          fontFamily: 'var(--font-sans)',
          whiteSpace: 'nowrap',
        }}>{date}</Typography>
      </Box>
      <Markdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ node, children, ...props }) => (
            <h1
              {...props}
              style={{
                margin: '0 0 20px',
                fontSize: '1.5em',
                fontWeight: 'bold',
                borderBottom: '1px solid #CCC'
              }}
            >
              {children}
            </h1>
          ),
          h2: ({ node, children, ...props }) => (
            <h2
              {...props}
              style={{
                margin: '0 0 20px',
                fontSize: '1.5em',
                borderBottom: '1px solid #CCC',
                fontWeight: 'bold'
              }}
            >
              {children}
            </h2>
          ),
          h3: ({ node, children, ...props }) => (
            <h3
              {...props}
              style={{
                margin: '0 0 20px',
                fontSize: '18px',
                borderBottom: '1px solid #CCC',
                fontWeight: 'bold'
              }}
            >
              {children}
            </h3>
          ),
          h4: ({ node, children, ...props }) => (
            <h4
              {...props}
              style={{
                margin: '0 0 20px',
                fontSize: '1.17em',
                fontWeight: 'bold',
                borderBottom: '1px solid #CCC'
              }}
            >
              {children}
            </h4>
          ),
          h5: ({ node, children, ...props }) => (
            <h5
              {...props}
              style={{
                margin: '0 0 20px',
                fontSize: '1em',
                fontWeight: 'bold',
                borderBottom: '1px solid #CCC'
              }}
            >
              {children}
            </h5>
          ),
          h6: ({ node, children, ...props }) => (
            <h6
              {...props}
              style={{
                margin: '0 0 10px',
                fontSize: '0.83em',
                fontWeight: 'bold',
                borderBottom: '0.25px solid #3a3a3a',
                paddingBottom: '2px'
              }}
            >
              {children}
            </h6>
          ),
          p: ({ node, children, ...props }) => (
            <p
              {...props}
              style={{ lineHeight: '1.5', marginTop: '0px' }}
            >
              {children}
            </p>
          ),
          blockquote: ({ node, children, ...props }) => (
            <blockquote
              {...props}
              style={{
                backgroundColor: 'grey',
                border: 'dashed 1px white',
                width: '97.5%',
                fontStyle: 'italic',
                textAlign: 'justify',
                padding: '2ch 1ch',
                margin: '0 0 2ch 0',
                color: 'var(--fg3)',
                fontSize: '13px',
                lineHeight: 1.4,
                fontWeight: 400,
                letterSpacing: 0
              }}
            >
              {children}
            </blockquote>
          ),
          a: ({ node, children, ...props }) => (
            <a
              {...props}
              style={{ color: '#74c0fc' }}
            >
              {children}
            </a>
          ),
          table: ({ node, children, ...props }) => (
            <div
              style={{
                width: '100%',
                overflowX: 'auto',
                display: 'flex',
                justifyContent: 'center',
                margin: '0 0 1rem 0'
              }}
            >
              <table
                {...props}
                style={{
                  borderCollapse: 'collapse',
                  width: 'min(100%, 900px)',
                  tableLayout: 'fixed',
                  fontSize: '0.74rem',
                  color: '#ffffff'
                }}
              >
                {children}
              </table>
            </div>
          ),
          th: ({ node, children, ...props }) => (
            <th
              {...props}
              style={{
                border: 'none',
                borderBottom: '1px solid #4a4a4a',
                padding: '0.5rem',
                textAlign: 'left',
                color: '#ffffff',
                fontWeight: 'bold'
              }}
            >
              {children}
            </th>
          ),
          td: ({ node, children, ...props }) => (
            <td
              {...props}
              style={{
                border: 'none',
                borderBottom: '1px solid #4a4a4a',
                padding: '0.5rem',
                color: '#ffffff',
                wordBreak: 'break-word'
              }}
            >
              {children}
            </td>
          ),
          code({ inline, className, children, ...props }) {
            if (inline) {
              return (
                <code
                  {...props}
                  style={{
                    fontSize: '0.95em',
                    padding: '0.1rem 0.3rem',
                    borderRadius: 4,
                    background: 'rgba(127,127,127,0.12)'
                  }}
                >
                  {children}
                </code>
              )
            }
            if (className && className.includes('language-heatmap')) {
              return <HeatmapChart raw={String(children).replace(/\n$/, '')} />
            }
            if (className && className.includes('language-mermaid')) {
              return <MermaidChart raw={String(children).replace(/\n$/, '')} />
            }
            return <CodeBlock className={className}>{children}</CodeBlock>
          }
        }}
      >
        {content}
      </Markdown>
    </div>
  )
}

export default Page
